import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { IAgent } from '../../../utils/types/IAgent';
import ConfigAgentModal from '../../modals/wizard/ConfigAgentModal';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface AgentGridProps {
    agents: IAgent[];
}

const AgentGrid: React.FC<AgentGridProps> = ({ agents }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef<IAgent>[]>([
        { field: 'matricule', headerName: 'Matricule', sortable: true, filter: true },
        { field: 'nom', headerName: 'Nom complet', sortable: true, filter: true },
        { field: 'login', headerName: "Nom d'utilisateur", sortable: true, filter: true },
        {
            cellRenderer: (params: any) => <ConfigAgentModal agent={params.data} />,
            headerName: 'Actions',
        },
    ]);

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const columnKeys = visibleColumns.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `agents_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, [gridRef]);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: IAgent[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        // Construire exportData avec uniquement colonnes visibles
        const exportData = rowData.map((row) => {
            const rowObj: Record<string, any> = {};
            visibleColumns.forEach((col) => {
                const colDef = col.getColDef();
                const field = colDef.field;
                const header = colDef.headerName ?? field ?? '';
                if (!field) return;
                rowObj[header] = (row as any)[field];
            });
            return rowObj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Agents');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `agents_export_${timestamp}.xlsx`);
    }, [gridRef]);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        theme={myTheme}
                        getRowId={(params) => params.data.id.toString()}
                        rowData={agents}
                        columnDefs={columnDefs}
                        pagination={true}
                        domLayout="normal"
                        defaultColDef={{
                            resizable: true,
                            editable: false,
                            minWidth: 100,
                            flex: 1,
                        }}
                        suppressExcelExport={true}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        animateRows={true}
                        overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                    />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default AgentGrid;
